import { buildAposPlan } from "@/components/aposEngine/simpleEngine";

export const DEFAULT_IMPLEMENTATION_ANSWERS = {
  state: {
    energy: 3,
    stress: 3,
    emotion: 3,
    sleep: 3,
    recovery: 3,
    motivation: 3,
    clarity: 3,
    discomfort: 3,
  },
  inputs: {
    movement: 3,
    exercise: 3,
    outdoors: 3,
    nutrition: 3,
    hydration: 3,
    screens: 3,
    connection: 3,
    routine: 3,
  },
  capacity: {
    activity: 3,
    injury: 3,
    confidence: 3,
    tolerance: 3,
  },
  behaviors: [],
  current: {
    states: ["calm-steady"],
    intensity: 3,
    energy: 3,
    physicalReadiness: "light-moderate",
    hydration: "ready",
    nourishment: "ready",
    urge: "none",
    conditioningLevel: "general",
    environment: "inside",
    minutes: 20,
  },
  schedule: {
    availability: {
      start: "5-10",
      middle: "10-20",
      end: "20-30",
      bedtime: "5-10",
    },
    times: {
      start: "08:00",
      middle: "12:30",
      end: "18:30",
      bedtime: "22:00",
    },
    bedtime: "22:00",
    bedtimeAvailability: "5-10",
    stressWindow: "midday",
    recoveryWindow: "evening",
    stressDays: ["MO", "TU", "WE", "TH", "FR"],
    socialPreference: "either",
    resources: ["outside", "safe-walk", "indoor-space"],
    familiarActivities: ["aerobic", "resistance"],
    competitionResponse: "focus-engage",
    preferredActivities: ["walking"],
    activityIntent: "begin",
    scheduledParticipation: "none",
    scheduledActivities: [],
    conditioningLevel: "general",
  },
};

const BASELINE_STATE_BY_PROFILE = {
  "high-activation": "restless-activated",
  "reduced-capacity": "depleted",
  "recovery-focus": "calm-steady",
  "regulation-building": "calm-steady",
  "growth-performance": "positive-energized",
};

const baselineCurrent = (profile, answers) => ({
  source: "baseline-assessment",
  states: [BASELINE_STATE_BY_PROFILE[profile?.key] || "calm-steady"],
  intensity: 3,
  energy: 3,
  physicalReadiness: "light-moderate",
  hydration: "unsure",
  nourishment: "unsure",
  urge: "none",
  conditioningLevel:
    answers?.schedule?.conditioningLevel ||
    answers?.current?.conditioningLevel ||
    "general",
  environment: "inside",
  minutes: 20,
});

export function buildImplementationPlan({
  profile,
  mapping,
  answers = DEFAULT_IMPLEMENTATION_ANSWERS,
  now = new Date(),
  currentOverride,
  fallbackLevel,
}) {
  const baselineOnly =
    answers?.current?.source === "app-check-in" ||
    !Array.isArray(answers?.current?.states) ||
    answers.current.states.length === 0;
  const effectiveAnswers = baselineOnly
    ? { ...answers, current: baselineCurrent(profile, answers) }
    : answers;
  const plan = buildAposPlan({
    profile,
    mapping,
    answers: effectiveAnswers,
    now,
    currentOverride,
    fallbackLevel,
  });

  if (!baselineOnly) return plan;

  const baselineName = mapping?.stateName || profile?.title || "Adaptive baseline";
  const baselineDescription =
    mapping?.stateDescription ||
    profile?.body ||
    "The assessment provides the longer-term baseline pattern.";
  return {
    ...plan,
    currentState: {
      ...plan.currentState,
      labels: ["Assessment baseline"],
      system: baselineName,
      summary: `${baselineName} · complete an app check-in for today’s state`,
      source: "baseline-assessment",
    },
    stateInterpretation: {
      ...plan.stateInterpretation,
      system: baselineName,
      participantExplanation: baselineDescription,
      clinicianExplanation:
        mapping?.professional?.interpretation || plan.stateInterpretation.clinicianExplanation,
    },
    readiness: {
      ...plan.readiness,
      title: "Confirm today’s physical readiness in APOS Companion",
      reason:
        "The baseline assessment no longer repeats daily state, readiness, hydration, nourishment, or behavioural-urge questions. Complete a current-state check-in before using the suggested dose.",
    },
    exerciseEligibility: {
      ...plan.exerciseEligibility,
      title: "Confirm today’s physical readiness in APOS Companion",
      reason:
        "Daily exercise eligibility is set by the in-app current-state check-in, not by the longer-term baseline assessment.",
    },
    immediate: {
      ...plan.immediate,
      rationale:
        `${baselineDescription} This is a baseline-matched starting option; today’s check-in confirms the appropriate delivery method and dose.`,
      adjustment:
        "Open APOS Companion and complete a current-state check-in before performing the activity. The app will preserve the baseline while matching today’s state and physical readiness.",
    },
    bottomLine: {
      happening:
        `${profile?.body || baselineDescription} This assessment describes the longer-term pattern rather than claiming what your nervous system is doing at this exact moment.`,
      shameRelease:
        "The reported behaviours are treated as information about learned adaptation and current conditions, not as character judgement.",
      whyPlanHelps:
        `${mapping?.input?.nervousSystemPurpose || plan.bottomLine.whyPlanHelps} The app check-in then adjusts the immediate output without rewriting the assessment baseline.`,
      overallSuggestion:
        "Use the assessment as the starting framework. Before activity, complete the app check-in so state, readiness, hydration, nourishment, environment, and time can shape the immediate output.",
      consultation: plan.bottomLine.consultation,
    },
  };
}

const pad = (value) => String(value).padStart(2, "0");

const icsDateTime = (date, time) => {
  const [year, month, day] = date.split("-");
  const [hour = "12", minute = "00"] = (time || "12:00").split(":");
  return `${year}${month}${day}T${pad(hour)}${pad(minute)}00`;
};

const addMinutes = (date, time, minutes) => {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = (time || "12:00").split(":").map(Number);
  const value = new Date(year, month - 1, day, hour, minute + Number(minutes || 0), 0);
  return `${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(
    value.getDate(),
  )}T${pad(value.getHours())}${pad(value.getMinutes())}00`;
};

const escapeIcs = (value) =>
  String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const calendarTitle = (session) =>
  `Adaptive Physiology · ${session.period}: ${session.activity}`;

const calendarDetails = (session) =>
  [
    session.preparation ? `Preparation: ${session.preparation}` : null,
    session.activityOptions?.length
      ? `Options:\n${session.activityOptions.map((option) => `• ${option}`).join("\n")}`
      : null,
    session.recovery ? `Recovery: ${session.recovery}` : null,
    session.alternative ? `Alternative: ${session.alternative}` : null,
    "Full instructions are available in your Adaptive Profile.",
  ]
    .filter(Boolean)
    .join("\n");

export function buildCalendarIcs(plan, now = new Date()) {
  const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(
    now.getUTCDate(),
  )}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(
    now.getUTCSeconds(),
  )}Z`;
  const events = (plan.week?.sessions || []).flatMap((session, index) => [
    "BEGIN:VEVENT",
    `UID:${session.id}-${index + 1}@adaptivephysiology`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${icsDateTime(session.date, session.time)}`,
    `DTEND:${addMinutes(session.date, session.time, session.durationMinutes)}`,
    `SUMMARY:${escapeIcs(calendarTitle(session))}`,
    `DESCRIPTION:${escapeIcs(calendarDetails(session))}`,
    "END:VEVENT",
  ]);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//FORR Health//Adaptive Physiology Assessment//EN",
    "CALSCALE:GREGORIAN",
    ...events,
    "END:VCALENDAR",
  ].join("\r\n");
}

const findCalendarSession = (plan, selector) => {
  const normalized = String(selector || "").toLowerCase();
  return (plan.week?.sessions || []).find((item) => {
    const calendarGroup = String(item.calendarGroup || "").toLowerCase();
    const period = String(item.period || "").toLowerCase();
    return (
      item.id === selector ||
      item.calendarGroup === selector ||
      calendarGroup.startsWith(`${normalized}-`) ||
      period.startsWith(normalized)
    );
  });
};

export function buildGoogleCalendarUrl(plan, selector) {
  const session = selector
    ? findCalendarSession(plan, selector)
    : plan.week?.sessions?.[0];
  if (!session) return null;
  const start = icsDateTime(session.date, session.time);
  const end = addMinutes(session.date, session.time, session.durationMinutes);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: calendarTitle(session),
    dates: `${start}/${end}`,
    details: calendarDetails(session),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildOutlookCalendarUrl(plan, selector) {
  const session = selector
    ? findCalendarSession(plan, selector)
    : plan.week?.sessions?.[0];
  if (!session) return null;
  const [year, month, day] = session.date.split("-").map(Number);
  const [hour, minute] = (session.time || "12:00").split(":").map(Number);
  const start = new Date(year, month - 1, day, hour, minute, 0);
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + Number(session.durationMinutes || 0));
  const params = new URLSearchParams({
    rru: "addevent",
    subject: calendarTitle(session),
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: calendarDetails(session),
  });
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timeZone) params.set("timezone", timeZone);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function buildCalendarProviderLinks(plan, provider) {
  const build =
    provider === "outlook" ? buildOutlookCalendarUrl : buildGoogleCalendarUrl;
  return (plan.week?.sessions || [])
    .map((session) => ({
      calendarGroup: session.id,
      label: `${session.day} · ${session.period}`,
      url: build(plan, session.id),
    }))
    .filter((item) => item.url);
}

export function downloadCalendarIcs(plan) {
  if (typeof document === "undefined") return;
  const blob = new Blob([buildCalendarIcs(plan)], {
    type: "text/calendar;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = "adaptive-physiology-one-week-plan.ics";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
